import { createContext, useContext, useMemo } from 'react';

export const BusinessMetaContext = createContext(null);
/** @deprecated Prefer Catalog / Purchases / Valuation slices — kept for merged bridge. */
export const InventoryDataContext = createContext(null);
/** Products, lots, stock locations — POS/catalog hot path (stable when tickets/recipes/purchases change). */
export const CatalogDataContext = createContext(null);
/** Product/lot/stock setters only — stable when products[] or lots[] change (checkout, write hooks). */
export const ProductsWritersContext = createContext(null);
/** Tables, open tickets, stock consumptions — restaurant table/kitchen ops. */
export const RestaurantOpsDataContext = createContext(null);
/** Recipes + production orders — food-cost / dish BOM (stable for POS product grid). */
export const RecipesDataContext = createContext(null);
/** Purchases list + writers — Achats updates must not thrash POS catalog subscribers. */
export const PurchasesDataContext = createContext(null);
/** inventoryData valuation / calculating flag — separate from catalog rows. */
export const InventoryValuationContext = createContext(null);
export const SalesDataContext = createContext(null);
/** Setters only — stable when `sales[]` changes (POS checkout writers). */
export const SalesWritersContext = createContext(null);
export const CustomersDataContext = createContext(null);
/** Setters only — stable when customer lists change. */
export const CustomersWritersContext = createContext(null);
export const SuppliersDataContext = createContext(null);
export const OrdersDataContext = createContext(null);
export const ExpensesDataContext = createContext(null);
export const PaymentsDataContext = createContext(null);
export const CashRegisterDataContext = createContext(null);
export const OperationsDataContext = createContext(null);
export const SyncDataContext = createContext(null);

function useSliceContext(Context, label) {
  const value = useContext(Context);
  if (!value) {
    throw new Error(`${label} must be used within DataProvider`);
  }
  return value;
}

export function useBusinessMeta() {
  return useSliceContext(BusinessMetaContext, 'useBusinessMeta');
}

/**
 * Shell catalog is data-only. Writers live on ProductsWritersContext.
 * Setters remain on the catalog object for back-compat dual paths that still
 * destructure both (prefer useProductsWriters for thrash isolation).
 */
export function useCatalogData() {
  return useSliceContext(CatalogDataContext, 'useCatalogData');
}

/** Product/lot writers only — does not re-render when products[] or lots[] identity changes. */
export function useProductsWriters() {
  return useSliceContext(ProductsWritersContext, 'useProductsWriters');
}

export function useRestaurantOpsData() {
  return useSliceContext(RestaurantOpsDataContext, 'useRestaurantOpsData');
}

export function useRecipesData() {
  return useSliceContext(RecipesDataContext, 'useRecipesData');
}

export function usePurchasesData() {
  return useSliceContext(PurchasesDataContext, 'usePurchasesData');
}

export function useInventoryValuation() {
  return useSliceContext(InventoryValuationContext, 'useInventoryValuation');
}

/**
 * Full inventory merge — re-renders when any inventory slice changes.
 * Prefer useCatalogData / usePurchasesData / useInventoryValuation on hot paths.
 */
export function useInventoryData() {
  const catalog = useCatalogData();
  const purchases = usePurchasesData();
  const valuation = useInventoryValuation();
  return useMemo(
    () => ({
      ...catalog,
      ...purchases,
      ...valuation
    }),
    [catalog, purchases, valuation]
  );
}

export function useSalesData() {
  return useSliceContext(SalesDataContext, 'useSalesData');
}

export function useSalesWriters() {
  return useSliceContext(SalesWritersContext, 'useSalesWriters');
}

export function useCustomersData() {
  return useSliceContext(CustomersDataContext, 'useCustomersData');
}

export function useCustomersWriters() {
  return useSliceContext(CustomersWritersContext, 'useCustomersWriters');
}

export function useSuppliersData() {
  return useSliceContext(SuppliersDataContext, 'useSuppliersData');
}

export function useOrdersData() {
  return useSliceContext(OrdersDataContext, 'useOrdersData');
}

export function useExpensesData() {
  return useSliceContext(ExpensesDataContext, 'useExpensesData');
}

export function usePaymentsData() {
  return useSliceContext(PaymentsDataContext, 'usePaymentsData');
}

export function useCashRegisterData() {
  return useSliceContext(CashRegisterDataContext, 'useCashRegisterData');
}

export function useOperationsData() {
  return useSliceContext(OperationsDataContext, 'useOperationsData');
}

export function useSyncData() {
  return useSliceContext(SyncDataContext, 'useSyncData');
}

/** Legacy: merges all slices — subscribers re-render when any slice changes. */
export function useMergedDataSlices() {
  const meta = useBusinessMeta();
  const inventory = useInventoryData();
  const restaurantOps = useRestaurantOpsData();
  const recipes = useRecipesData();
  const sales = useSalesData();
  const customers = useCustomersData();
  const suppliers = useSuppliersData();
  const orders = useOrdersData();
  const expenses = useExpensesData();
  const payments = usePaymentsData();
  const cashRegister = useCashRegisterData();
  const operations = useOperationsData();
  const sync = useSyncData();

  return useMemo(
    () => ({
      ...meta,
      ...inventory,
      ...restaurantOps,
      ...recipes,
      ...sales,
      ...customers,
      ...suppliers,
      ...orders,
      ...expenses,
      ...payments,
      ...cashRegister,
      ...operations,
      ...sync
    }),
    [
      meta,
      inventory,
      restaurantOps,
      recipes,
      sales,
      customers,
      suppliers,
      orders,
      expenses,
      payments,
      cashRegister,
      operations,
      sync
    ]
  );
}
